import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Select } from 'antd';
import {
	DndContext,
	DragOverlay,
	MouseSensor,
	pointerWithin,
	rectIntersection,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';

import { DataState, Skeleton, deriveDataState } from '../loading';
import { ItemCard } from './ItemCardsGrid';
import BoardItemActionsMenu from './BoardItemActionsMenu';
import BoardFillModal from './BoardFillModal';
import ChoiceChips from './ChoiceChips';
import LookupRefs from '../common/LookupRefs';
import UserValue from '../users/UserValue';
import AttestationModal from '../users/AttestationModal';
import useBoardMove from '../../hooks/useBoardMove';
import useRankMove from '../../hooks/useRankMove';
import { sortByRank } from '../../utils/rank';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import useEnsureCards from '../../hooks/useEnsureCards';
import { MAP_MAX_ITEMS } from '../../hooks/useMapItems';
import { cellEditability } from './cardTableModel';
import { createPrimaryDateResolver } from '../../utils/cards';
import getItemDisplayValue from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { canManage as canManageCollection, canWriteCard } from '../../utils/permissions';
import { LINKED_ROW_EXCLUDED_ACTIONS, isLinkedRow, itemHomeCollectionId } from '../../utils/linkedRecords';
import { rowOwnCollection } from '../../utils/recordQualifier';
import { RATE_LIMITED_TEXT } from '../../utils/feedMessages';
import { boardEligible, columnKeyOf, layoutBoard, movePatch, neighbourColumn } from '../../utils/board';

const number = n => Number(n || 0).toLocaleString('en-US');
const itemsWord = n => `${number(n)} ${Number(n) === 1 ? 'item' : 'items'}`;
const cellId = (laneKey, columnKey) => `${laneKey}::${columnKey}`;

const CONTROL = 'button, a[href], [role="button"], input, select, textarea, summary';
/**
 * Enter or Space on a control of the board (Show more, Retry, a column
 * switch) presses it; the page's list keys (Enter opens the open item's
 * page) must not act as well. A card itself keeps the grid's behaviour.
 */
function keepKeysHere(event) {
	const target = event.target;
	if (!(target instanceof Element) || target.classList.contains('item-card')) return;
	if (target.closest(CONTROL) && (event.key === 'Enter' || event.key === ' ')) event.stopPropagation();
}

/**
 * What the board says about what it holds, in one or two sentences: how many
 * items are on it, and — when the list holds more than the board has read —
 * how many are not shown.
 */
export function boardSummary({ loaded, total, max = MAP_MAX_ITEMS }) {
	if (!loaded) return '';
	const parts = [`${itemsWord(loaded)} on the board.`];
	if (total > loaded) {
		const rest = total - loaded;
		parts.push(loaded >= max
			? `The board holds the first ${number(max)} of ${number(total)}; ${itemsWord(rest)} are not shown. Narrow the filter to see the rest.`
			: `${number(rest)} more ${rest === 1 ? 'is' : 'are'} not on the board yet.`);
	}
	return parts.join(' ');
}

/** The element's definition on this item's own card (a child card reads its parent's). */
function defOn(element, item, getElementsForCard) {
	if (!element) return null;
	const cardId = normalizeId(item?.card_id);
	const known = element.defs?.get(cardId);
	if (known) return known;
	const defs = typeof getElementsForCard === 'function' ? getElementsForCard(cardId) || [] : [];
	return defs.find(def => def.name === element.name && def.dataType === element.dataType) || null;
}

/** A column's (or lane's) name, drawn as its value reads everywhere else. */
function ValueLabel({ element, column, resolveLookupValue, collectionId }) {
	if (!column || column.other) return <span className="items-board__value-label">{column?.label || 'Other'}</span>;
	const def = element?.defs ? [...element.defs.values()][0] : null;
	if (element.dataType === 'choice') return <ChoiceChips element={def} value={column.value} />;
	if (element.dataType === 'user') return <UserValue element={def} value={column.value} collectionId={collectionId} size="xs" />;
	return <LookupRefs element={def} value={column.value} resolveLookupValue={resolveLookupValue} size="xs" />;
}

function columnText(element, column, resolveLookupValue) {
	if (!column) return '';
	if (column.other || element?.dataType === 'choice') return column.label || '';
	if (element?.dataType === 'card-lookup' && typeof resolveLookupValue === 'function') return resolveLookupValue(column.value) || column.label || '';
	return column.label || '';
}

function BoardCell({ id, lane, column, refuse, children, empty }) {
	const { setNodeRef, isOver } = useDroppable({ id, data: { lane, column }, disabled: Boolean(refuse) });
	const classes = [
		'items-board__cell',
		isOver ? 'items-board__cell--over' : '',
		refuse ? 'items-board__cell--refuse' : '',
	].filter(Boolean).join(' ');
	return (
		<div ref={setNodeRef} className={classes} data-board-cell={id}>
			{children}
			{empty ? <div className="items-board__cell-empty" aria-hidden="true" /> : null}
		</div>
	);
}

function BoardTile({ id, item, draggable, onKeyDown, children, dragging, place = null }) {
	const { setNodeRef, listeners } = useDraggable({ id, data: { item }, disabled: !draggable });
	// A manual order (KPR-1396): the tile is also where a card can be dropped
	// to land just before (or after) it.
	const { setNodeRef: setDropRef } = useDroppable({ id: `tile:${id}`, data: { tile: item, ...(place || {}) }, disabled: !place });
	const ref = useCallback((node) => { setNodeRef(node); setDropRef(node); }, [setNodeRef, setDropRef]);
	return (
		// The tile itself is the item's button (ItemCard); this wrapper only
		// carries the pointer drag and the board's own keys.
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div
			ref={ref}
			className={`items-board__tile${dragging ? ' items-board__tile--dragging' : ''}${draggable ? ' items-board__tile--draggable' : ''}`}
			data-board-item={id}
			onKeyDown={onKeyDown}
			{...(draggable ? listeners : {})}
		>
			{children}
		</div>
	);
}

/**
 * The list's BOARD view (KPR-1395): one column per value of an element, a
 * card per item drawn from its card's tile layout (`ItemCard`), with optional
 * swimlanes by a second element. Dragging a card (a mouse; dnd-kit's
 * `MouseSensor`, so a touch tap stays a tap) or **Move to…** in its ⋯ (every
 * device: a phone has no drag, and it is the keyboard's way, as is
 * Shift+← / Shift+→ on a focused card) writes the element through the item's
 * ordinary update (`useBoardMove`). On a phone one column shows at a time,
 * with a control to switch.
 *
 *   <ItemsBoard source={useMapItems(…)} by={element} lanes={element|null} … />
 *
 * `source` is the list's own question read whole (`hooks/useMapItems`, or a
 * combined list's pages): its items, its total and its read state, drawn with
 * the §14 states (`DataState`).
 */
export default function ItemsBoard({
	source,
	by,
	lanes = null,
	cardsById,
	getElementsForCard,
	tileLayouts = null,
	resolvePrimaryDate: resolvePrimaryDateOverride = null,
	resolveLookupValue,
	collection = null,
	collectionId,
	selectedItemId = null,
	onSelectItem,
	expandedItemId = null,
	renderExpanded,
	onItemUpdated,
	onItemDeleted,
	onItemRemoved,
	isMobile = false,
	crossCollection = false,
	emptyText = 'No items match',
	// A list sorted by a manual order (KPR-1396, utils/rank.js): `{ element,
	// direction }`. Each column then keeps that order, and a card dropped on
	// another lands just before (or after) it; ⋯ offers Move up / down / to top.
	rank = null,
	// () — read the board again (a renumbered order, a move someone beat).
	onReload,
}) {
	useEnsureCards(source?.items || []);
	const board = useBoardMove({
		getElementsForCard,
		onItemUpdated,
		onItemRemoved,
		attachmentsOff: collection ? collection.allowAttachments === false : false,
	});
	const { pending } = board;
	const items = useMemo(
		() => (source?.items || []).map(item => pending[normalizeId(item?._id)] || item),
		[source?.items, pending]
	);

	const labelFor = useCallback((element, key) => {
		if (element?.dataType === 'card-lookup' && typeof resolveLookupValue === 'function') return resolveLookupValue(key) || '';
		return '';
	}, [resolveLookupValue]);
	// A combined list's rows live in several collections: no manual order there.
	const rankOn = Boolean(rank) && !crossCollection;
	const ordered = useMemo(() => (rankOn ? sortByRank(items, rank) : items), [items, rank, rankOn]);
	const layout = useMemo(() => layoutBoard({ items: ordered, by, lanes, labelFor }), [ordered, by, lanes, labelFor]);
	const rankOrder = useRankMove({
		rank: rankOn ? rank : null,
		rows: ordered,
		collection,
		collectionId,
		cardsById,
		getElementsForCard,
		onItemUpdated,
		onReload,
	});
	const itemById = useMemo(() => new Map(items.map(item => [normalizeId(item?._id), item])), [items]);

	const resolvePrimaryDate = useMemo(
		() => resolvePrimaryDateOverride || createPrimaryDateResolver(cardsById || {}),
		[resolvePrimaryDateOverride, cardsById]
	);
	const { tags: tagVocabulary } = useCollectionTags(collectionId, { enabled: !crossCollection });
	const privateTags = useOptionalTags()?.tags || null;
	const tagContext = useMemo(() => ({ vocabulary: tagVocabulary, privateTags }), [tagVocabulary, privateTags]);

	// Why this card cannot be moved here, or null: the table's own rule for
	// editing its value in place (no write on the card, a locked record, a
	// see-only element, a linked row), for the column element and the lanes.
	const blockedReason = useCallback((item) => {
		const check = (element) => {
			if (!element) return null;
			const def = defOn(element, item, getElementsForCard);
			if (!def) return 'This item’s card has no such field.';
			// On a combined list the board's element is the list's; this
			// item's own may hold several values, which a column cannot.
			if (!boardEligible(def)) return `This item’s ${element.label || element.name} cannot be set from a board here. Change it from the item.`;
			const verdict = cellEditability({ column: { element: def.name }, def, item, collection: crossCollection ? null : collection, cardsById });
			return verdict.editable ? null : verdict.reason;
		};
		return check(by) || check(lanes);
	}, [by, lanes, getElementsForCard, collection, cardsById, crossCollection]);

	// ---- the move -----------------------------------------------------------

	const [announce, setAnnounce] = useState('');
	const focusAfterRef = useRef(null);
	const titleOf = useCallback((item) => {
		const card = cardsById?.[normalizeId(item?.card_id)] || null;
		const defs = typeof getElementsForCard === 'function' ? getElementsForCard(item?.card_id) || [] : [];
		return getItemDisplayValue({ card, elementDefs: defs, item, resolveLookupValue }) || 'The item';
	}, [cardsById, getElementsForCard, resolveLookupValue]);

	const moveTo = useCallback(async (item, column, lane = null, { refocus = false } = {}) => {
		const reason = blockedReason(item);
		const id = normalizeId(item?._id);
		const where = columnText(by, column, resolveLookupValue);
		if (reason) {
			setAnnounce(`Not moved. ${reason}`);
			return;
		}
		const patch = movePatch({
			item,
			def: defOn(by, item, getElementsForCard),
			laneDef: lanes ? defOn(lanes, item, getElementsForCard) : null,
			by,
			lanes,
			column,
			lane,
			columns: layout.columns,
			laneColumns: layout.lanes,
		});
		if (!patch) return;
		if (patch.refused) {
			setAnnounce(`Not moved. ${patch.refused}`);
			return;
		}
		if (refocus) focusAfterRef.current = id;
		const result = await board.move(item, patch.elements, { label: where });
		// A move that did not happen (the fill form, a refusal) leaves focus where it is.
		if (!result?.ok) focusAfterRef.current = null;
		if (result?.ok) setAnnounce(`Moved ${titleOf(item)} to ${where}.`);
		else if (result?.reason === 'refused' || result?.reason === 'invalid') setAnnounce(`Not moved. ${result.message || ''}`.trim());
		return result;
	}, [blockedReason, by, lanes, getElementsForCard, board, titleOf, resolveLookupValue, layout]);

	// Focus stays on the card it moved: it is drawn in another column now.
	useEffect(() => {
		const id = focusAfterRef.current;
		if (!id) return;
		const node = document.querySelector(`[data-board-item="${id}"] .item-card`);
		if (node) {
			focusAfterRef.current = null;
			node.focus();
		}
	});

	const laneOf = useCallback((item) => {
		if (!lanes) return null;
		const key = columnKeyOf(lanes, item, layout.lanes);
		return layout.lanes.find(entry => entry.key === key) || null;
	}, [lanes, layout]);

	// The cards a card sits among: its column in its lane (KPR-1396).
	const cellOf = useCallback((item) => {
		const laneKey = lanes ? columnKeyOf(lanes, item, layout.lanes) : null;
		const lane = layout.lanes.find(entry => (lanes ? entry.key === laneKey : true)) || layout.lanes[0];
		return lane?.cells.get(columnKeyOf(by, item, layout.columns)) || [];
	}, [lanes, by, layout]);

	const handleTileKeys = useCallback((event, item) => {
		// Shift+↑ / Shift+↓: up or down the column, in a manual order.
		if (rankOn && event.shiftKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown') && !event.metaKey && !event.ctrlKey && !event.altKey) {
			const target = event.target;
			if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]')) return;
			event.preventDefault();
			event.stopPropagation();
			focusAfterRef.current = normalizeId(item?._id);
			rankOrder.step(item, event.key === 'ArrowUp' ? 'up' : 'down', cellOf(item)).then((result) => {
				if (result?.ok) setAnnounce(`Moved ${titleOf(item)} ${event.key === 'ArrowUp' ? 'up' : 'down'}.`);
				else if (result?.reason === 'nothing') setAnnounce(event.key === 'ArrowUp' ? 'It is first in its column.' : 'It is last in its column.');
			});
			return;
		}
		if (!event.shiftKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
		if (event.metaKey || event.ctrlKey || event.altKey) return;
		const target = event.target;
		if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]')) return;
		event.preventDefault();
		event.stopPropagation();
		const next = neighbourColumn(layout, by, item, event.key === 'ArrowRight' ? 1 : -1);
		if (!next) {
			setAnnounce(event.key === 'ArrowRight' ? 'It is in the last column.' : 'It is in the first column.');
			return;
		}
		moveTo(item, next, laneOf(item), { refocus: true });
	}, [layout, by, moveTo, laneOf, rankOn, rankOrder, cellOf, titleOf]);

	// ---- the drag -----------------------------------------------------------

	const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }));
	const [activeId, setActiveId] = useState(null);
	const activeItem = activeId ? itemById.get(activeId) || null : null;
	const refusesFor = useCallback((column, lane) => {
		if (!activeItem) return false;
		const patch = movePatch({
			item: activeItem,
			def: defOn(by, activeItem, getElementsForCard),
			laneDef: lanes ? defOn(lanes, activeItem, getElementsForCard) : null,
			by, lanes, column, lane,
			columns: layout.columns,
			laneColumns: layout.lanes,
		});
		return Boolean(patch?.refused);
	}, [activeItem, by, lanes, getElementsForCard, layout]);
	const collision = useCallback(args => {
		const hits = pointerWithin(args);
		// A card over a card (a manual order): the card it is over, first.
		const tiles = hits.filter(hit => `${hit.id}`.startsWith('tile:') && `${hit.id}` !== `tile:${args.active?.id}`);
		if (tiles.length) return tiles;
		const cells = hits.filter(hit => !`${hit.id}`.startsWith('tile:'));
		return cells.length ? cells : rectIntersection(args).filter(hit => !`${hit.id}`.startsWith('tile:'));
	}, []);
	const onDragStart = useCallback(({ active }) => setActiveId(`${active.id}`), []);
	const onDragCancel = useCallback(() => setActiveId(null), []);
	const onDragEnd = useCallback(async ({ active, over }) => {
		setActiveId(null);
		const item = itemById.get(`${active?.id}`);
		if (!item || !over?.data?.current) return;
		const { column, lane, tile } = over.data.current;
		const sameCell = column?.key === columnKeyOf(by, item, layout.columns)
			&& (!lanes || !lane || lane.all || lane.key === columnKeyOf(lanes, item, layout.lanes));
		if (!rankOn || (!tile && !sameCell)) {
			moveTo(item, column, lane && !lane.all ? lane : null);
			return;
		}
		// A manual order: the card lands beside the card it was dropped on —
		// before it over its upper half, after it over the lower — or last in
		// its own column when dropped on the column's empty space. Into another
		// column, the column is written first; then the place.
		const cell = (lane?.cells?.get(column?.key)) || [];
		let anchor = tile;
		let side = 'before';
		if (tile) {
			const middle = over.rect ? over.rect.top + over.rect.height / 2 : null;
			const dropped = active?.rect?.current?.translated;
			const at = dropped ? dropped.top + dropped.height / 2 : null;
			if (middle !== null && at !== null && at > middle) side = 'after';
		} else {
			anchor = [...cell].reverse().find(row => normalizeId(row?._id) !== normalizeId(item?._id)) || null;
			side = 'after';
		}
		let current = item;
		if (!sameCell) {
			const result = await moveTo(item, column, lane && !lane.all ? lane : null);
			if (!result?.ok) return;
			// The place is written over the version the column write left.
			current = result.item || item;
		}
		if (!anchor) return;
		const moved = await rankOrder.moveBeside(current, anchor, side, cell);
		if (moved?.ok) setAnnounce(`Moved ${titleOf(item)}.`);
	}, [itemById, moveTo, by, lanes, layout, rankOn, rankOrder, titleOf]);

	// ---- the phone's one column ------------------------------------------

	const [phoneColumn, setPhoneColumn] = useState(null);
	const shownColumns = useMemo(() => {
		if (!isMobile) return layout.columns;
		const found = layout.columns.find(column => column.key === phoneColumn);
		return found ? [found] : layout.columns.slice(0, 1);
	}, [isMobile, layout.columns, phoneColumn]);

	// ---- drawing ------------------------------------------------------------

	const tileFor = (item) => {
		const id = normalizeId(item?._id);
		const cardId = normalizeId(item?.card_id);
		const card = cardsById?.[cardId] || null;
		const elementDefs = typeof getElementsForCard === 'function' ? getElementsForCard(item?.card_id) || [] : [];
		const blocked = blockedReason(item);
		const linked = isLinkedRow(item);
		const current = columnKeyOf(by, item, layout.columns);
		const lane = laneOf(item);
		const targets = layout.columns.map(column => ({
			key: column.key,
			label: columnText(by, column, resolveLookupValue) || column.label || '—',
			current: column.key === current,
			disabled: Boolean(movePatch({ item, def: defOn(by, item, getElementsForCard), by, column, columns: layout.columns })?.refused),
			column,
		}));
		const inPending = Boolean(pending[id]);
		return (
			<BoardTile
				key={id}
				id={id}
				item={item}
				place={rankOn && !isMobile ? { column: layout.columns.find(c => c.key === current) || null, lane: lane || layout.lanes[0] } : null}
				draggable={!isMobile && !blocked && !inPending}
				dragging={activeId === id}
				onKeyDown={event => handleTileKeys(event, item)}
			>
				<ItemCard
					item={item}
					card={card}
					elementDefs={elementDefs}
					resolveLookupValue={resolveLookupValue}
					primaryDate={resolvePrimaryDate(cardId)}
					tileLayout={tileLayouts?.[cardId] || null}
					selected={Boolean(selectedItemId) && id === selectedItemId}
					onSelect={onSelectItem}
					expanded={Boolean(expandedItemId) && id === expandedItemId}
					renderExpanded={renderExpanded}
					collection={linked ? null : collection}
					collectionId={itemHomeCollectionId(item, collectionId) || rowOwnCollection(item)}
					canEdit={linked || crossCollection ? false : (collection ? canWriteCard(collection, cardId) : true)}
					canManage={linked || crossCollection ? false : Boolean(collection && canManageCollection(collection))}
					cardsById={cardsById}
					onDeleted={onItemDeleted}
					onClose={() => onSelectItem?.(null)}
					tagContext={tagContext}
					onItemUpdated={onItemUpdated}
					ownCollection={crossCollection ? rowOwnCollection(item) : null}
					actionsMenu={BoardItemActionsMenu}
					actionsMenuProps={{
						moveTargets: targets,
						moveDisabled: blocked,
						exclude: linked ? LINKED_ROW_EXCLUDED_ACTIONS : undefined,
						onMove: target => moveTo(item, target.column, lane, { refocus: true }),
						extraActions: rankOn ? rankOrder.menuActions(item, cellOf(item)) : undefined,
					}}
				/>
			</BoardTile>
		);
	};

	const header = column => (
		<div className={`items-board__head${column.other ? ' items-board__head--other' : ''}`} key={`head-${column.key}`} role="columnheader">
			<span className="items-board__head-label">
				<ValueLabel element={by} column={column} resolveLookupValue={resolveLookupValue} collectionId={collectionId} />
			</span>
			<span className="items-board__count" aria-label={itemsWord(column.count)}>{number(column.count)}</span>
		</div>
	);

	const loaded = source?.items?.length || 0;
	const hasData = loaded > 0;
	const firstLoadFailed = Boolean(source?.error) && !hasData;
	const state = deriveDataState({
		loading: Boolean(source?.loading) && !hasData,
		error: firstLoadFailed ? source.error : null,
		hasData,
		isEmpty: !source?.loading && !source?.error && !hasData,
	});
	const errorText = source?.error?.kind === 'rate_limited'
		? RATE_LIMITED_TEXT
		: source?.error?.kind === 'refused'
			? 'This filter could not be run. Change it above.'
			: null;
	const summary = boardSummary({ loaded, total: source?.total || 0 });

	const phoneSwitch = isMobile && layout.columns.length ? (
		<Select
			className="items-board__column-switch"
			aria-label={`${by?.label || 'Column'} shown`}
			value={shownColumns[0]?.key}
			onChange={setPhoneColumn}
			options={layout.columns.map(column => ({
				value: column.key,
				label: (
					<span className="items-board__switch-option">
						<ValueLabel element={by} column={column} resolveLookupValue={resolveLookupValue} collectionId={collectionId} />
						<span className="items-board__count">{number(column.count)}</span>
					</span>
				),
			}))}
		/>
	) : null;

	const gridStyle = { '--board-columns': shownColumns.length };
	const grid = (
		<div className="items-board__scroll">
			<div className="items-board__grid" style={gridStyle} role="table" aria-label={`Board by ${by?.label || ''}`.trim()}>
				{isMobile ? null : <div className="items-board__heads" role="row">{shownColumns.map(header)}</div>}
				{layout.lanes.map((lane) => {
					// A phone shows one column: a lane counts what it holds there.
					const laneCount = isMobile
						? shownColumns.reduce((sum, column) => sum + (lane.cells.get(column.key) || []).length, 0)
						: lane.count;
					return (
					<div className="items-board__lane" key={lane.key} role="rowgroup">
						{lane.all ? null : (
							<div className="items-board__lane-head" role="row">
								<span className="items-board__lane-label">
									<ValueLabel element={lanes} column={lane} resolveLookupValue={resolveLookupValue} collectionId={collectionId} />
								</span>
								<span className="items-board__count" aria-label={itemsWord(laneCount)}>{number(laneCount)}</span>
							</div>
						)}
						<div className="items-board__row" role="row">
							{shownColumns.map((column) => {
								const cell = lane.cells.get(column.key) || [];
								return (
									<BoardCell
										key={column.key}
										id={cellId(lane.key, column.key)}
										lane={lane}
										column={column}
										refuse={activeItem ? refusesFor(column, lane.all ? null : lane) : false}
										empty={!cell.length}
									>
										{cell.map(tileFor)}
									</BoardCell>
								);
							})}
						</div>
					</div>
					);
				})}
			</div>
		</div>
	);

	const partialFailure = source?.error && hasData ? (
		<Alert
			type="warning"
			showIcon
			className="items-board__alert"
			message={source.error.kind === 'rate_limited'
				? `${RATE_LIMITED_TEXT} The board shows the first ${itemsWord(loaded)}.`
				: `The board stopped after the first ${itemsWord(loaded)}. The rest could not be loaded.`}
			action={source.error.kind === 'refused' || !source.retry ? null : <Button size="small" onClick={source.retry}>Retry</Button>}
		/>
	) : null;

	const fill = board.fill;
	const fillCollectionId = fill ? itemHomeCollectionId(fill.item, collectionId) || rowOwnCollection(fill.item) : collectionId;

	return (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<section className={`items-board${isMobile ? ' items-board--phone' : ''}`} aria-label="Board view" onKeyDown={keepKeysHere}>
			<p className="items-board__summary" role="status">{hasData && !source?.loading ? summary : ''}</p>
			<p className="items-board__announce" aria-live="polite">{announce}</p>
			{partialFailure}
			<DataState
				state={state}
				hasData={hasData}
				isEmpty={state === 'empty'}
				skeleton={<Skeleton variant="block" height={240} />}
				empty={<div className="items-board__empty">{emptyText}</div>}
				error={errorText}
				onRetry={source?.error?.kind === 'refused' ? undefined : source?.retry}
				label="the board's items"
			>
				{phoneSwitch}
				<DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
					{grid}
					<DragOverlay dropAnimation={null}>
						{activeItem ? (
							<div className="items-board__overlay">
								<ItemCard
									item={activeItem}
									card={cardsById?.[normalizeId(activeItem.card_id)] || null}
									elementDefs={typeof getElementsForCard === 'function' ? getElementsForCard(activeItem.card_id) || [] : []}
									resolveLookupValue={resolveLookupValue}
									primaryDate={resolvePrimaryDate(normalizeId(activeItem.card_id))}
									tileLayout={tileLayouts?.[normalizeId(activeItem.card_id)] || null}
									readOnly
									tagContext={tagContext}
								/>
							</div>
						) : null}
					</DragOverlay>
				</DndContext>
				{source?.hasMore ? (
					<div className="items-board__more">
						<Button onClick={source.loadMore} loading={source.loadingMore}>Show more on the board</Button>
					</div>
				) : null}
			</DataState>
			{fill ? (
				<BoardFillModal
					fill={fill}
					cardsById={cardsById}
					getElementsForCard={getElementsForCard}
					resolveLookupValue={resolveLookupValue}
					onSubmit={async (values) => {
						const item = fill.item;
						const result = await board.submitFill(values);
						if (result?.ok) setAnnounce(`Moved ${titleOf(item)} to ${fill.label}.`);
					}}
					onCancel={() => { board.cancelFill(); setAnnounce('Not moved.'); }}
				/>
			) : null}
			{rankOrder.attestation ? (
				<AttestationModal
					open
					mode={rankOrder.attestation.mode}
					collectionId={collectionId}
					cardId={rankOrder.attestation.cardId}
					onSigned={(token) => { const { onSigned } = rankOrder.attestation; rankOrder.setAttestation(null); onSigned(token); }}
					onClose={() => { const { onCancel } = rankOrder.attestation; rankOrder.setAttestation(null); onCancel?.(); }}
				/>
			) : null}
			{board.attestation ? (
				<AttestationModal
					open
					mode={board.attestation.mode}
					collectionId={fillCollectionId}
					cardId={board.attestation.cardId}
					onSigned={(token) => { const { onSigned } = board.attestation; board.setAttestation(null); onSigned(token); }}
					onClose={() => { const { onCancel } = board.attestation; board.setAttestation(null); onCancel?.(); }}
				/>
			) : null}
		</section>
	);
}
